// BYO-01: the forms and lists shared by My subscriptions (person scope) and Setup › Keys &
// connections (company scope). A key typed into a form lives in that form's state only and is
// cleared on save; the API seals it and answers with its last four characters, which is all any
// list ever shows.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  Field,
  FieldLabel,
  HomeCardRow,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  SettingsGroup,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BanIcon, KeyRoundIcon, PlugIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { ConfirmRowAction } from "../confirm-row-action";
import { formatDate } from "../format";
import { problemText } from "../settings/problem-text";
import { COPY, PROVIDERS, providerLabel } from "./copy";
import { CredentialEditButton } from "./credential-edit";
import { type Credential, subscriptionKeys } from "./queries";

type Scope = "person" | "company";

/** SETUP-UX: a row's secondary actions show on hover or focus (always on touch screens). */
const ON_HOVER =
  "opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100";

/** The add form (or why it is closed) at the foot of a list group. */
function GroupFoot({ children }: { children: ReactNode }) {
  return (
    <div data-slot="group-foot" className="border-t border-subtle p-4">
      {children}
    </div>
  );
}

function Tick({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal leading-snug">
        {children}
      </Label>
    </div>
  );
}

/** Revoke / disconnect, confirmed first (a destructive step: spec §6 AlertDialog). */
export function ConfirmButton({
  label,
  title,
  description,
  onConfirm,
  pending,
  className,
}: {
  label: string;
  title: string;
  description: string;
  onConfirm: () => void;
  pending: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        className={className}
        onClick={() => setOpen(true)}
        disabled={pending}
      >
        {label}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription>{description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setOpen(false);
                onConfirm();
              }}
            >
              {label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---- model credentials -------------------------------------------------------------------------

export function KeyForm({
  api,
  co,
  scope,
  disabled,
}: {
  api: Api;
  co: string;
  scope: Scope;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const ids = { provider: useId(), label: useId(), key: useId() };
  const [provider, setProvider] = useState<string>("anthropic");
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [own, setOwn] = useState(false);
  const [ownWork, setOwnWork] = useState(scope === "person");
  const [terms, setTerms] = useState(false);
  const spec = PROVIDERS.find((p) => p.value === provider) ?? PROVIDERS[0];
  const add = useMutation({
    mutationFn: () =>
      api.client.modelCredentials.create({
        params: { co },
        idempotencyKey: newIdempotencyKey(),
        body: {
          kind: "api_key",
          scope,
          provider,
          label: label.trim(),
          api_key: key,
          attestation: {
            terms_ref: spec.terms,
            plan_is_own: own,
            own_work_only: ownWork,
            allows_paid_client_work: false,
          },
        },
      }),
    onSuccess: (r) => {
      setKey("");
      setLabel("");
      setOwn(false);
      setTerms(false);
      void qc.invalidateQueries({ queryKey: subscriptionKeys.credentials(co) });
      toast.add({ title: `Key saved (ends in ${r.credential.key_last4})`, type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "The key was not saved."), type: "error" }),
  });
  // Own-work-only is asked of a person's key; a company key is the company's by definition.
  const ready =
    !disabled &&
    label.trim() &&
    key.trim().length >= 8 &&
    own &&
    terms &&
    (scope === "company" || ownWork);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) add.mutate();
  };
  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3"
      aria-label={scope === "company" ? "Add a company API key" : "Add your API key"}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field>
          <FieldLabel htmlFor={ids.provider}>Provider</FieldLabel>
          <NativeSelect
            id={ids.provider}
            className="w-full"
            value={provider}
            disabled={disabled}
            onChange={(e) => setProvider(e.target.value)}
          >
            {PROVIDERS.map((p) => (
              <NativeSelectOption key={p.value} value={p.value}>
                {p.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.label}>Name</FieldLabel>
          <Input
            id={ids.label}
            value={label}
            placeholder={scope === "company" ? "Company Anthropic key" : "My Anthropic key"}
            disabled={disabled}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Field>
      </div>
      <Field>
        <span className="flex items-center gap-1">
          <FieldLabel htmlFor={ids.key}>API key</FieldLabel>
          <InfoTip label="About the API key">{COPY.keyNotice}</InfoTip>
        </span>
        <Input
          id={ids.key}
          className="font-mono"
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={key}
          disabled={disabled}
          onChange={(e) => setKey(e.target.value)}
        />
      </Field>
      <div className="flex flex-col gap-2">
        <Tick checked={own} onChange={setOwn}>
          {scope === "company"
            ? "This key belongs to this company's own provider account."
            : "This key is mine, from my own provider account."}
        </Tick>
        {scope === "person" ? (
          <Tick checked={ownWork} onChange={setOwnWork}>
            I use it only for my own work (my own agents and crew).
          </Tick>
        ) : null}
        <Tick checked={terms} onChange={setTerms}>
          I have read {providerLabel(provider)}'s terms for API use.
        </Tick>
      </div>
      <div>
        <Button type="submit" size="sm" disabled={!ready || add.isPending}>
          <KeyRoundIcon data-icon="inline-start" aria-hidden="true" />
          Save key
        </Button>
      </div>
    </form>
  );
}

export function CredentialList({
  api,
  co,
  rows,
  title,
  empty,
  children,
}: {
  api: Api;
  co: string;
  rows: Credential[];
  title: string;
  empty: string;
  /** Under the rows: the add form, or why adding is closed. */
  children?: ReactNode;
}) {
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: (c: Credential) =>
      api.client.modelCredentials.revoke({
        params: { co, id: c.id },
        idempotencyKey: newIdempotencyKey(),
        body: {},
      }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: subscriptionKeys.credentials(co) });
      toast.add({ title: `${c.label} revoked`, type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not revoked."), type: "error" }),
  });
  const live = rows.filter((r) => r.state === "active");
  return (
    <SettingsGroup title={title} count={live.length}>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">{empty}</p>
      ) : (
        rows.map((c) => (
          <HomeCardRow
            key={c.id}
            className="group/row"
            data-credential={c.id}
            title={<span className="font-medium">{c.label}</span>}
            description={
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{providerLabel(c.provider)}</span>
                {c.kind === "api_key" ? (
                  <span className="font-mono">••••{c.key_last4}</span>
                ) : (
                  <span>
                    Own CLI login{c.plan ? ` · ${c.plan}` : ""} ·{" "}
                    {c.login_location === "user_device" ? "your device" : "your runtime session"}
                  </span>
                )}
                <span className="max-sm:hidden">added {formatDate(c.created_at)}</span>
              </span>
            }
            trailing={
              c.state === "active" ? (
                <span className="flex items-center gap-1">
                  <CredentialEditButton api={api} co={co} credential={c} className={ON_HOVER} />
                  <ConfirmRowAction
                    className={ON_HOVER}
                    icon={BanIcon}
                    label={`Revoke ${c.label}`}
                    tip="Revoke"
                    title={`Revoke ${c.label}?`}
                    description={
                      c.kind === "api_key"
                        ? "The sealed key is destroyed now. Nothing can use it again; add a new key to replace it."
                        : "The record of your CLI login is ended. Nothing was ever stored to destroy."
                    }
                    confirmLabel="Revoke"
                    disabled={revoke.isPending}
                    onConfirm={() => revoke.mutate(c)}
                  />
                </span>
              ) : (
                <Badge variant="outline">Revoked</Badge>
              )
            }
          />
        ))
      )}
      {children ? <GroupFoot>{children}</GroupFoot> : null}
    </SettingsGroup>
  );
}

// ---- tool connections --------------------------------------------------------------------------

export function ConnectionForm({
  api,
  co,
  scope,
  disabled,
  onDone,
}: {
  api: Api;
  co: string;
  scope: Scope;
  disabled: boolean;
  /** INC2-CONTACT: after a connection is made (the Catalog's modal closes). */
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const ids = {
    label: useId(),
    name: useId(),
    host: useId(),
    header: useId(),
    scheme: useId(),
    probe: useId(),
    key: useId(),
  };
  const [f, setF] = useState({
    label: "",
    name: "",
    host: "",
    header: "Authorization",
    scheme: "Bearer",
    probe: "/",
    key: "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const add = useMutation({
    mutationFn: () =>
      api.client.connections.create({
        params: { co },
        idempotencyKey: newIdempotencyKey(),
        body: {
          scope,
          provider: "api_key",
          label: f.label.trim(),
          config: {
            name: f.name.trim().toUpperCase(),
            host: f.host.trim(),
            position: { in: "header", name: f.header.trim(), scheme: f.scheme.trim() || null },
            probe_path: f.probe.trim() || "/",
          },
          api_key: f.key,
        },
      }),
    onSuccess: (c) => {
      setF((x) => ({ ...x, key: "", label: "", name: "", host: "" }));
      void qc.invalidateQueries({ queryKey: subscriptionKeys.connections(co) });
      toast.add({ title: `${c.label} connected (key ends in ${c.key_last4})`, type: "success" });
      onDone?.();
    },
    onError: (e) => toast.add({ title: problemText(e, "Not connected."), type: "error" }),
  });
  const ready = !disabled && f.label.trim() && f.name.trim() && f.host.trim() && f.key.length >= 8;
  const field = (k: keyof typeof f, label: string, hint?: string, mono = false) => (
    <Field>
      {/* UX-A: the hint sits behind an (i) beside the label, not under the field. */}
      <span className="flex items-center gap-1">
        <FieldLabel htmlFor={ids[k]}>{label}</FieldLabel>
        {hint ? <InfoTip label={`About ${label}`}>{hint}</InfoTip> : null}
      </span>
      <Input
        id={ids[k]}
        className={mono ? "font-mono" : undefined}
        value={f[k]}
        disabled={disabled}
        onChange={set(k)}
        {...(k === "key" ? { type: "password", autoComplete: "off", spellCheck: false } : {})}
      />
    </Field>
  );
  return (
    <form
      className="flex flex-col gap-3"
      aria-label={scope === "company" ? "Connect a company tool" : "Connect a tool"}
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) add.mutate();
      }}
    >
      <div className="grid gap-4 md:grid-cols-2">
        {field("label", "Name", "What you call it, such as KPI Beast.")}
        {field("name", "Key name", "A–Z, 0–9 and _, such as KPI_BEAST.", true)}
        {field("host", "Host", "The one public host it may reach, such as api.example.com.", true)}
        {field("probe", "Check path", "A read-only path the check calls, such as /health.", true)}
        {field("header", "Header", undefined, true)}
        {field("scheme", "Scheme", "Bearer, Token, or empty for the bare key.", true)}
      </div>
      {field("key", "API key", undefined, true)}
      <div>
        <Button type="submit" size="sm" disabled={!ready || add.isPending}>
          <PlugIcon data-icon="inline-start" aria-hidden="true" />
          Connect
        </Button>
      </div>
    </form>
  );
}
